import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { API_BASE } from "@/api/client";
import {
  useApprovePublish,
  useCurrentProjectId,
  useOverview,
  usePublishProject,
  usePublishReadiness,
  useRejectPublish,
  useSession,
  useUnpublishProject,
  useUpdateProject,
  useUploadCover,
} from "@/api/queries";
import type { ActivityEntry, OverviewProgress, PublishCheck } from "@/api/types";
import { Avatar, type AvatarRole } from "@/components/Avatar";
import { Button } from "@/components/Button";
import { ErrorState } from "@/components/ErrorState";
import { Field, TextInput } from "@/components/Field";
import { Panel } from "@/components/Panel";
import { ProgressRow } from "@/components/Meter";
import { RemoteState } from "@/components/RemoteState";
import { ScoreRing } from "@/components/ScoreRing";
import { Tag } from "@/components/Tag";
import { formatNumber } from "@/lib/cn";

/** 后端 role → 原型头像角色。 */
const AVATAR_ROLE: Record<string, AvatarRole> = {
  head_writer: "founder",
  writer: "writer",
  producer: "producer",
  ai: "ai",
};

function avatarRole(role: string): AvatarRole {
  return AVATAR_ROLE[role] ?? "writer";
}

function formatMoment(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * 项目总览（原型 `renderOverview`）。
 *
 * 全部数值来自 `GET /projects/{id}/overview` 的只读聚合：
 * 健康度、产物计数、推进度、协作轨迹、待研究缺口与额度。
 * 没有后端就没有数据——不再回落到基线数值（CONSTITUTION D1 / D2）。
 *
 * @dataSource remote
 */
export function OverviewView() {
  const navigate = useNavigate();
  const projectId = useCurrentProjectId();
  const overview = useOverview(projectId);
  const readiness = usePublishReadiness(projectId);
  const publish = usePublishProject(projectId);
  const approve = useApprovePublish(projectId);
  const reject = useRejectPublish(projectId);
  const unpublish = useUnpublishProject(projectId);
  const session = useSession();
  const uploadCover = useUploadCover(projectId);

  const data = overview.data;
  const counts = data?.counts ?? {};
  const health = data?.health;
  const blockingIssues = (data?.issues ?? []).filter((issue) => issue.severity === "error");
  // 三态上架状态（`ADR-022`）：唯一来源是后端的 `publish_status`，不据 `is_published` 反推。
  const publishStatus = data?.project.publish_status ?? "unpublished";
  const isPublished = publishStatus === "published";
  const isPending = publishStatus === "pending";
  const ready = readiness.data?.ready ?? false;
  // 谁能批准/驳回由 `P-1` 的批准角色决定（`specs/08` §5）；后端仍会二次校验（不靠前端隐藏按钮）。
  const canApprove =
    session.data?.platform_role === "editor" &&
    (session.data?.creation_role === "head_writer" || session.data?.creation_role === "admin");

  const update = useUpdateProject(projectId);
  const [synopsis, setSynopsis] = useState("");
  // 简介从总览聚合回传（不额外请求 `GET /projects/{id}`）；数据到手后回填输入框。
  useEffect(() => {
    setSynopsis(data?.project.synopsis ?? "");
  }, [data?.project.synopsis]);
  const synopsisDirty = synopsis !== (data?.project.synopsis ?? "");

  return (
    <div className="page-scroll">
      <div className="page-width">
        <RemoteState queries={[overview]} title="项目总览加载失败">
          {data ? (
            <>
              <header className="page-heading">
                <div>
                  <span className="eyebrow">
                    {data.project.name} · 当前阶段 {data.project.current_stage || "—"}
                  </span>
                  <h2>{data.project.name} 的实时生产状态</h2>
                </div>
                <div className="meta-row">
                  <Tag tone={health?.blocking ? "red" : "green"}>
                    {health?.blocking ? "存在阻塞" : "结构稳定"}
                  </Tag>
                  <Tag tone="gold">{data.pending_gates} 个关口待处理</Tag>
                  <Tag tone="blue">{data.project.methodology_version}</Tag>
                </div>
              </header>

              <section className="kpi-strip" aria-label="项目关键数据">
                <div className="kpi">
                  <span>剧情锚点</span>
                  <strong>{counts.anchors ?? 0}</strong>
                  <small>共享主干</small>
                </div>
                <div className="kpi">
                  <span>Storylet</span>
                  <strong>{counts.storylets ?? 0}</strong>
                  <small>分支单元</small>
                </div>
                <div className="kpi">
                  <span>配置结局</span>
                  <strong>{counts.endings ?? 0}</strong>
                  <small>需全部可达</small>
                </div>
                <div className="kpi">
                  <span>待研究缺口</span>
                  <strong>{data.canon_gaps.length}</strong>
                  <small>关闭前不得写入正式版</small>
                </div>
              </section>

              <div className="overview-grid">
                <Panel
                  title="发布到故事广场"
                  description={
                    isPublished
                      ? "已上架：观众可在故事广场试玩这部作品"
                      : isPending
                        ? "待审核：已提交上架申请，等待 head_writer / admin 批准"
                        : "申请审核后，由 head_writer / admin 批准即可上架试玩"
                  }
                  actions={
                    <Tag tone={isPublished ? "green" : isPending ? "blue" : "gold"}>
                      {isPublished ? "已上架" : isPending ? "待审核" : "未发布"}
                    </Tag>
                  }
                >
                  {readiness.isError ? (
                    <ErrorState title="发布就绪加载失败" error={readiness.error} />
                  ) : null}
                  {readiness.data ? (
                    <ul className="issue-list">
                      {readiness.data.checks.map((check: PublishCheck) => (
                        <li className="issue-item" key={check.id}>
                          <span className="mini-mark">{check.ok ? "✓" : "!"}</span>
                          <div>
                            <p>{check.message}</p>
                            {!check.ok && check.suggestion ? (
                              <small>{check.suggestion}</small>
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  <Field
                    id="project-synopsis"
                    label="一句话简介"
                    hint="发布后显示在故事广场的作品卡片上；留空即不展示"
                  >
                    <TextInput
                      id="project-synopsis"
                      value={synopsis}
                      maxLength={200}
                      placeholder="例如：一个关于边界与代价的宋世故事"
                      onChange={(event) => setSynopsis(event.target.value)}
                    />
                  </Field>
                  {update.isError ? <ErrorState title="简介保存失败" error={update.error} /> : null}
                  <div className="meta-row" style={{ marginTop: 8 }}>
                    <Button
                      size="small"
                      variant="quiet"
                      disabled={!synopsisDirty || update.isPending}
                      onClick={() => update.mutate({ synopsis })}
                    >
                      {update.isPending ? "保存中…" : "保存简介"}
                    </Button>
                  </div>

                  <Field
                    id="project-cover"
                    label="封面"
                    hint="PNG / JPEG / WebP，≤ 2 MiB；留空则广场卡片不显示封面（ADR-023）"
                  >
                    <input
                      id="project-cover"
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={(event) => {
                        const picked = event.target.files?.[0];
                        if (picked) uploadCover.mutate(picked);
                        event.target.value = ""; // 允许连续选同一个文件
                      }}
                    />
                  </Field>
                  {data?.project.cover_url ? (
                    <img
                      className="cover-preview"
                      src={`${API_BASE}${data.project.cover_url}`}
                      alt="作品封面预览"
                    />
                  ) : null}
                  {uploadCover.isPending ? <p className="inline-note">封面上传中…</p> : null}
                  {uploadCover.isError ? (
                    <ErrorState title="封面上传失败" error={uploadCover.error} />
                  ) : null}
                  {publish.isError ? (
                    <ErrorState title="申请上架失败" error={publish.error} />
                  ) : null}
                  {approve.isError ? (
                    <ErrorState title="批准上架失败" error={approve.error} />
                  ) : null}
                  {reject.isError ? <ErrorState title="驳回上架失败" error={reject.error} /> : null}
                  {unpublish.isError ? (
                    <ErrorState title="下架失败" error={unpublish.error} />
                  ) : null}
                  <div className="meta-row" style={{ marginTop: 12 }}>
                    {isPublished ? (
                      <Button
                        size="small"
                        variant="quiet"
                        disabled={unpublish.isPending}
                        onClick={() => unpublish.mutate()}
                      >
                        {unpublish.isPending ? "下架中…" : "下架"}
                      </Button>
                    ) : isPending ? (
                      <>
                        <Button
                          size="small"
                          variant="quiet"
                          disabled={unpublish.isPending}
                          onClick={() => unpublish.mutate()}
                        >
                          {unpublish.isPending ? "撤回中…" : "撤回申请"}
                        </Button>
                        {canApprove ? (
                          <>
                            <Button
                              size="small"
                              variant="quiet"
                              disabled={reject.isPending}
                              onClick={() => reject.mutate("")}
                            >
                              {reject.isPending ? "驳回中…" : "驳回"}
                            </Button>
                            <Button
                              size="small"
                              variant="primary"
                              disabled={approve.isPending}
                              onClick={() => approve.mutate("")}
                            >
                              {approve.isPending ? "批准中…" : "批准上架"}
                            </Button>
                          </>
                        ) : (
                          <Tag tone="blue">等待审核</Tag>
                        )}
                      </>
                    ) : (
                      <Button
                        size="small"
                        variant="primary"
                        disabled={!ready || publish.isPending}
                        onClick={() => publish.mutate()}
                      >
                        {publish.isPending ? "申请中…" : "申请上架"}
                      </Button>
                    )}
                  </div>
                </Panel>
                <Panel
                  title="项目健康度"
                  description={`校验 ${health?.errors ?? 0} 阻断 · ${health?.warnings ?? 0} 告警`}
                  actions={
                    <Button size="small" variant="quiet" onClick={() => navigate("/studio/checks")}>
                      查看问题
                    </Button>
                  }
                  bodyClassName="health-layout"
                >
                  <ScoreRing score={health?.score ?? 0} caption="结构健康度" />
                  <div className="progress-list">
                    {data.progress.map((row: OverviewProgress) => (
                      <ProgressRow
                        key={row.type}
                        label={row.label}
                        sublabel={`${row.approved}/${row.total} 已批准`}
                        value={Math.round(row.ratio * 100)}
                      />
                    ))}
                  </div>
                </Panel>

                <Panel
                  title="待处理问题"
                  description="最近一次确定性校验的结果"
                  actions={
                    <Tag tone={blockingIssues.length ? "red" : "green"}>{data.issues.length}</Tag>
                  }
                >
                  {data.issues.length === 0 ? (
                    <p className="inline-note">当前没有未解决的校验问题。</p>
                  ) : (
                    <ul className="issue-list">
                      {data.issues.map((issue, index) => (
                        <li className="issue-item" key={`${issue.rule_id}-${index}`}>
                          <span className="mini-mark">
                            {issue.severity === "error" ? "!" : "?"}
                          </span>
                          <div>
                            <p>
                              {issue.rule_id} · {issue.message}
                            </p>
                            <small>{issue.suggestion}</small>
                          </div>
                          <span aria-hidden="true">›</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Panel>

                <Panel title="产物计数" description={`共 ${counts.artifacts ?? 0} 个产物`}>
                  <div className="progress-list">
                    <ProgressRow
                      label="分场"
                      sublabel={`${counts.scenes ?? 0} 个`}
                      value={Math.min(100, (counts.scenes ?? 0) * 2)}
                    />
                    <ProgressRow
                      label="选择节点"
                      sublabel={`${counts.choices ?? 0} 个`}
                      value={Math.min(100, (counts.choices ?? 0) * 5)}
                    />
                    <ProgressRow
                      label="状态定义"
                      sublabel={`${counts.states ?? 0} 个`}
                      value={Math.min(100, (counts.states ?? 0) * 3)}
                    />
                  </div>
                </Panel>

                <Panel title="最近协作" description="关口与评审记录">
                  {data.activity.length === 0 ? (
                    <p className="inline-note">暂无审批记录。</p>
                  ) : (
                    <ul className="activity-list">
                      {data.activity.slice(0, 6).map((item: ActivityEntry) => (
                        <li className="activity-item" key={`${item.gate}-${item.at}`}>
                          <Avatar role={avatarRole(item.role)}>
                            {(item.actor || "?").slice(-1).toUpperCase()}
                          </Avatar>
                          <div>
                            <p>
                              {item.gate} {item.decision}
                              {item.comment ? ` · ${item.comment}` : ""}
                            </p>
                            <small>
                              {item.actor} · {formatMoment(item.at)}
                            </small>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </Panel>
              </div>

              {blockingIssues.length > 0 ? (
                <div className="conflict-banner" style={{ marginTop: 20 }}>
                  <span className="mini-mark" aria-hidden="true">
                    !
                  </span>
                  <p>
                    当前有 {blockingIssues.length} 项阻塞问题未解决。按
                    PR-126，下游产物在此状态下不得标记为正式版本；
                    <button
                      type="button"
                      className="button button-small button-quiet"
                      style={{ marginLeft: 8 }}
                      onClick={() => navigate("/studio/checks")}
                    >
                      前往结构检查
                    </button>
                  </p>
                </div>
              ) : null}

              <div className="section-title" style={{ marginTop: 24 }}>
                <div>
                  <h3>待研究缺口</h3>
                  <p>关闭前相关内容不得写入正式版本</p>
                </div>
              </div>
              <div className="panel">
                <div className="panel-body">
                  {data.canon_gaps.length === 0 ? (
                    <p className="inline-note">没有未关闭的 Canon 缺口。</p>
                  ) : (
                    <ul className="issue-list">
                      {data.canon_gaps.map((gap) => (
                        <li className="issue-item" key={gap.id}>
                          <span className="mini-mark">?</span>
                          <div>
                            <p>
                              {gap.id} · {gap.topic}
                            </p>
                            <small>阻塞：{gap.blocks.length ? gap.blocks.join(" / ") : "—"}</small>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="section-title" style={{ marginTop: 24 }}>
                <div>
                  <h3>额度</h3>
                  <p>成本为实时记账值，不是估算</p>
                </div>
              </div>
              <div className="panel">
                <div className="panel-body meta-row">
                  <Tag>已用 {formatNumber(data.cost.used)}</Tag>
                  <Tag tone="green">剩余 {formatNumber(data.cost.remaining)}</Tag>
                  <Tag>配额 {formatNumber(data.cost.quota)}</Tag>
                </div>
              </div>
            </>
          ) : null}
        </RemoteState>
      </div>
    </div>
  );
}
